import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { store } from 'Reducers';
import logger from 'Utils/Logger';
import { getCurrentAuthToken } from 'Utils/Auth';
import { reportError } from 'Utils/reportError';
import { getPlatformInfo, getDeviceClass, getViewport } from 'Actions/events';
import { EventType, PhraseNoteError } from 'Types/EventTypes';
import type EventStream from './EventStream';

// MUS-499: play-time per-note flush controller.
//
// Per-note rows are captured AS PLAYED (they already accumulate in the phrase's
// playData on the tick loop) and flushed asynchronously in batches, so a phrase the
// user stops on / half-plays / navigates away from no longer drops its notes.
//
// This is deliberately self-contained (its own transport, not the shared eventQueue)
// so it can (a) run its own 2s backstop cadence, and (b) use a keepalive/beacon send
// on teardown - without changing how any other event flushes.
//
// Triggers (wired by the caller):
//   - a 2s timer (crash/OOM backstop; most notes flush earlier on a boundary/pause)
//   - phrase boundary + pause          -> flush('boundary' | 'pause'), alive path
//   - visibility-hidden / app-inactive / pagehide -> flushOnTeardown(), keepalive path
//
// Idempotency: each flushed batch is one event carrying a client_event_id stamped
// ONCE when the batch is cut and stable across any resend (a network retry, or a
// teardown beacon re-sending an un-acked batch). The backend dedups on it via the
// shared event_dedup ledger (MUS-418). Complementarily, each note is serialized
// exactly once (the Flushed high-water mark on NoteEvent), so batches don't overlap
// under normal play - dedup only has to catch a true redelivery.
//
// Retry: a batch is retained and resent only while a resend can succeed - no
// response (network error), a 5xx, or a retryable 4xx. Any other 4xx is a permanent
// reject: the server would answer a resend the same way, so the POST's batches are
// dropped and reported once instead of being resent on every flush with each new
// batch piled on.
//
// MUS-636: a retained batch is not resent on every flush. Each one keeps its own
// count of failed sends and waits out a growing, jittered gap before its next try;
// after MAX_RESENDS failed resends it is dropped and reported once. A batch that is
// waiting does not hold back the flush: newer batches still go out on schedule. The
// stop flush sends every pending batch, waiting or not. A send that failed with no
// response while the device says it is offline waits like any other but does not
// count toward MAX_RESENDS, so notes played offline are kept until the network
// returns (bounded by MAX_RETAINED_BATCHES).

const FLUSH_INTERVAL_MS = 2000;

// MUS-636 numbers. A batch becomes due again 1-2s after its first failed send, so
// the first resend goes on the next flush tick when the failed POST was quick, or
// the tick after. The gap then doubles up to a ceiling of 30-60s. Eight resends
// span about 2 to 4 minutes of trying, which outlasts a database failover or a
// deploy rollover, while a batch the database will never take costs 9 POSTs instead
// of one every 2s for the rest of the page's life.
const MAX_RESENDS = 8;
const RESEND_BASE_DELAY_MS = 2000;
const RESEND_MAX_DELAY_MS = 60_000;

// MUS-636: the most batches kept at once. A batch holds the notes of one phrase
// played in one 2s window (a few KB), and a window with no notes cuts none, so
// this is roughly an hour of continuous play offline, a few MB. Past it the oldest
// batches are dropped and reported once until a send is accepted again.
const MAX_RETAINED_BATCHES = 1800;

// Equal jitter: half of the exponential step is fixed and half is random. The
// shortest possible gap doubles with each failed send until the ceiling, so the
// gaps grow on the whole; at the ceiling they vary between 30 and 60s. Players
// whose sends failed at the same moment spread their resends out instead of
// retrying in lockstep.
const resendDelayMs = (failedSends: number): number => {
  const step = Math.min(RESEND_MAX_DELAY_MS, RESEND_BASE_DELAY_MS * 2 ** (failedSends - 1));
  return step / 2 + Math.random() * (step / 2);
};

// 4xx statuses that mean "try again" rather than "this request is wrong": an auth
// token the interceptor could not refresh in time, a request timeout, a rate limit.
const RETRYABLE_CLIENT_ERROR_STATUSES = [401, 408, 429];

const isPermanentReject = (status: number): boolean =>
  status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERROR_STATUSES.includes(status);

// The slice of the events endpoint's partial-success summary this controller reads.
// A 422 carries it, with one failed[] entry per event the server could not persist.
// A 200 carries it too, with one skipped.invalid[] entry per event that failed
// validation and was not persisted.
type EventsPostSummary = {
  skipped?: { invalid?: Array<{ reason?: unknown }> };
  failed?: Array<{ reason?: unknown }>;
};

type FlushContext = {
  playSessionId: string;
  levelNumber: number;
  tierNumber: number;
  errorRecognitionMode: string; // 'MIDI' | 'AUDIO'
};

// One flushed play-time event: the wire payload plus its stable client_event_id,
// how many of its sends have failed (this sets the gap), how many of those count
// toward MAX_RESENDS (not the ones that failed offline), and the earliest time
// (epoch ms) it may be sent again (0 for a batch not sent yet).
type PendingBatch = {
  client_event_id: string;
  event: Record<string, unknown>;
  failedSends: number;
  countedResends: number;
  nextSendAt: number;
};

const isDeviceOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

export class PlayTimeNoteFlushController {
  // Pulls the resolved-unflushed rows grouped per phrase from the active stream.
  private getBatches:
    | ((lookback?: number) => { phraseS3Id: string; notes: PhraseNoteError[] }[])
    | null = null;
  // MUS-473: optional best-effort snapshot of the in-progress phrase at teardown.
  private getInterruptionSnapshot:
    | (() => { phraseS3Id?: string; position?: number } | null)
    | null = null;
  private context: FlushContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  // Un-acked flushed batches, keyed by client_event_id. Retained until a 2xx, a
  // permanent reject or the resend limit, then dropped. Re-sent on teardown / a later
  // flush with the SAME id so a redelivery dedups.
  private pending: Map<string, PendingBatch> = new Map();
  private sending = false;
  // MUS-636: set once MAX_RETAINED_BATCHES has dropped batches and been reported;
  // cleared when a send is accepted.
  private retainedLimitReported = false;
  // MUS-473: stable-per-INTERRUPTION-EPISODE id for the teardown SessionInterruption
  // event. One teardown fires flushOnTeardown() 2-3x (visibilitychange->hidden,
  // pagehide, and appStateChange->inactive on mobile); those share this id so the
  // MUS-418 backend dedup collapses them to a single interruption row. A foreground-
  // return (resetInterruptionEpisode) clears it, so a LATER background within the same
  // play session mints a fresh id and records as its OWN distinct interruption at its
  // own phrase - each genuine leave-point is preserved, rather than first-write-wins
  // freezing the whole session at the earliest one. Null until the first teardown of
  // an episode mints one; reset on foreground-return, session (re)configure, and stop.
  private interruptionClientEventId: string | null = null;

  private get eventsUrl(): string {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v1/events`;
  }

  private get enabled(): boolean {
    return process.env.REACT_APP_SEND_EVENTS === 'true';
  }

  // Called when a play session starts. Idempotent: reconfiguring restarts the timer.
  configure(
    getBatches: (lookback?: number) => { phraseS3Id: string; notes: PhraseNoteError[] }[],
    context: FlushContext,
    getInterruptionSnapshot?: () => { phraseS3Id?: string; position?: number } | null,
  ) {
    this.getBatches = getBatches;
    this.context = context;
    this.getInterruptionSnapshot = getInterruptionSnapshot ?? null;
    // A new session starts a fresh interruption episode.
    this.interruptionClientEventId = null;
    this.startTimer();
  }

  private startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => {
      this.flush('timer').catch((err) => logger.warn('[play-time-flush] timer flush failed', err));
    }, FLUSH_INTERVAL_MS);
  }

  private stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Called when a play session ends. Does a final alive flush, then detaches. Pending
  // batches survive the detach so a straggler retry (or teardown) can still send them.
  async stop() {
    this.stopTimer();
    await this.flush('stop').catch((err) =>
      logger.warn('[play-time-flush] stop flush failed', err),
    );
    this.getBatches = null;
    this.context = null;
    this.getInterruptionSnapshot = null;
    this.interruptionClientEventId = null;
  }

  // MUS-473: called when the app returns to the foreground (tab visible / app active).
  // Ends the current interruption episode so the NEXT teardown mints a fresh id. A
  // single teardown's 2-3 duplicate fires still share one id (they happen before any
  // return); a genuine background-then-return-then-background sequence records as two
  // distinct interruptions, each at its own in-progress phrase.
  resetInterruptionEpisode() {
    this.interruptionClientEventId = null;
  }

  private buildBase(): Record<string, unknown> {
    const sessionId = store.getState()?.eventReducer?.sessionId ?? '';
    return {
      datetime: new Date().toISOString(),
      analytics_session_id: sessionId,
      platform: getPlatformInfo(),
      device_class: getDeviceClass(),
      ...getViewport(),
    };
  }

  // Pull unflushed rows, cut one batch per phrase (each with its own client_event_id),
  // and add them to the pending set. Returns whether anything new was captured.
  private capture(lookback?: number): boolean {
    if (!this.getBatches || !this.context) return false;
    const groups = this.getBatches(lookback);
    if (!groups.length) return false;
    const base = this.buildBase();
    for (const group of groups) {
      const clientEventId = uuidv4();
      this.pending.set(clientEventId, {
        client_event_id: clientEventId,
        event: {
          ...base,
          client_event_id: clientEventId,
          event_type: EventType.PlayTimeNotesEvent,
          phrase_s3_id: group.phraseS3Id,
          level_number: this.context.levelNumber,
          tier_number: this.context.tierNumber,
          play_session_id: this.context.playSessionId,
          error_recognition_mode: this.context.errorRecognitionMode,
          notes: group.notes,
        },
        failedSends: 0,
        countedResends: 0,
        nextSendAt: 0,
      });
    }
    return true;
  }

  // MUS-378 RUM path: one error per permanently rejected POST, under the same
  // 'events.post' tag the shared queue reports its rejected entries with.
  private reportDroppedBatches(dropped: PendingBatch[], httpStatus: number, err: unknown) {
    const summary = axios.isAxiosError(err)
      ? (err.response?.data as EventsPostSummary | undefined)
      : undefined;
    const failed = summary?.failed ?? [];
    const firstReason = failed[0]?.reason;
    const detail = {
      httpStatus,
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      failedCount: failed.length,
      firstFailureReason: typeof firstReason === 'string' ? firstReason.slice(0, 120) : undefined,
    };
    reportError(
      new Error(`play-time notes POST rejected, batches dropped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // MUS-636: one error per failed POST that used up the last resend of one or more
  // batches. Counts only: no failure reason (a database error's text can carry the
  // values it was writing) and no note data.
  private reportResendsExhausted(dropped: PendingBatch[], httpStatus: number | undefined) {
    const detail = {
      httpStatus,
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      resends: MAX_RESENDS,
    };
    reportError(
      new Error(`play-time notes resend limit reached, batches dropped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // MUS-636: after a failed send a resend could fix, each batch in the POST counts
  // one more failed send, and one more toward MAX_RESENDS unless the send got no
  // response while the device is offline. A batch past MAX_RESENDS is dropped and
  // reported; the rest wait out their backoff. Map.set keeps each batch's place, so
  // resends go out in the order the batches were cut.
  private scheduleResends(sent: PendingBatch[], httpStatus: number | undefined, err: unknown) {
    const now = Date.now();
    const countsTowardLimit = !(httpStatus === undefined && isDeviceOffline());
    const counted = sent.map((b) => ({
      ...b,
      failedSends: b.failedSends + 1,
      countedResends: b.countedResends + (countsTowardLimit ? 1 : 0),
    }));
    const exhausted = counted.filter((b) => b.countedResends > MAX_RESENDS);
    counted
      .filter((b) => b.countedResends <= MAX_RESENDS)
      .forEach((b) =>
        this.pending.set(b.client_event_id, {
          ...b,
          nextSendAt: now + resendDelayMs(b.failedSends),
        }),
      );
    exhausted.forEach((b) => this.pending.delete(b.client_event_id));
    if (exhausted.length > 0) this.reportResendsExhausted(exhausted, httpStatus);
    logger.warn('[play-time-flush] alive flush failed; retaining for retry', err);
  }

  // MUS-636: keep at most MAX_RETAINED_BATCHES, dropping the oldest. Reported once
  // (counts only) until a send is accepted again.
  private dropOldestPastRetainedLimit() {
    const excess = this.pending.size - MAX_RETAINED_BATCHES;
    if (excess <= 0) return;
    const dropped = [...this.pending.values()].slice(0, excess);
    dropped.forEach((b) => this.pending.delete(b.client_event_id));
    if (this.retainedLimitReported) return;
    this.retainedLimitReported = true;
    const detail = {
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      limit: MAX_RETAINED_BATCHES,
    };
    reportError(
      new Error(
        `play-time notes retained batch limit reached, oldest dropped ${JSON.stringify(detail)}`,
      ),
      'events.post',
    );
  }

  // One error per accepted POST whose summary lists events skipped as invalid. The
  // server did not persist them and would skip a resend the same way, so they stay
  // acked; without this report the loss is silent on both sides.
  private reportInvalidSkips(
    summary: EventsPostSummary | undefined,
    httpStatus: number | undefined,
    sentBatches: number,
  ) {
    const invalid = summary?.skipped?.invalid ?? [];
    if (invalid.length === 0) return;
    const firstReason = invalid[0]?.reason;
    const detail = {
      httpStatus,
      sentBatches,
      invalidCount: invalid.length,
      firstInvalidReason:
        firstReason === undefined ? undefined : JSON.stringify(firstReason).slice(0, 120),
    };
    reportError(
      new Error(`play-time notes POST accepted, invalid batches skipped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // Alive-path flush (timer / boundary / pause / stop): capture, then POST the
  // pending batches that are due (new ones, and retained ones whose backoff has run
  // out) over the authenticated axios path (Bearer attached by the interceptor). On
  // 2xx, drop the batches that were sent, reporting any the server skipped as
  // invalid. On a permanent reject, drop and report them. Any other failure keeps
  // them for a later flush, after a backoff, up to MAX_RESENDS resends. The stop
  // flush is the session's last alive send: it sends every pending batch, waiting
  // or not, and a failure still counts toward MAX_RESENDS.
  async flush(reason: string): Promise<void> {
    if (!this.enabled) return;
    this.capture();
    if (this.sending) return;
    this.dropOldestPastRetainedLimit();
    const now = Date.now();
    const ignoreBackoff = reason === 'stop';
    const inFlight = [...this.pending.values()].filter((b) => ignoreBackoff || b.nextSendAt <= now);
    if (inFlight.length === 0) return;
    this.sending = true;
    try {
      const response = await axios.post<EventsPostSummary>(
        this.eventsUrl,
        inFlight.map((b) => b.event),
      );
      for (const b of inFlight) this.pending.delete(b.client_event_id);
      this.retainedLimitReported = false;
      this.reportInvalidSkips(response?.data, response?.status, inFlight.length);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status !== undefined && isPermanentReject(status)) {
        // The server would answer a resend the same way, and on the events
        // endpoint's own 422 it has already persisted every event it could. Nothing
        // in this POST is worth keeping: a retained batch would be resent on every
        // flush for the rest of the page's life.
        for (const b of inFlight) this.pending.delete(b.client_event_id);
        this.reportDroppedBatches(inFlight, status, err);
      } else {
        // Keep the batches pending; a later flush (or teardown) resends them. The
        // stable client_event_id means the resend dedups if the first actually landed.
        this.scheduleResends(inFlight, status, err);
      }
    } finally {
      this.sending = false;
    }
  }

  // Teardown-path flush (page hidden / app inactive / pagehide / unload). Captures a
  // wider lookback for safety, then sends all pending batches with a transport that
  // survives teardown: keepalive fetch (carries the Authorization header, so it hits
  // the same auth-guarded endpoint), falling back to sendBeacon only where fetch
  // keepalive is unavailable. Best-effort and non-blocking; pending is NOT cleared
  // (the page is going away - if it survives, the next alive flush resends and the
  // backend dedups).
  async flushOnTeardown(): Promise<void> {
    if (!this.enabled) return;
    this.capture(12);
    // MUS-473: a page teardown during play is an interruption. Emit one
    // SessionInterruptionEvent (its own client_event_id) alongside any pending
    // note batches, on the same keepalive/beacon path. Sent even when no notes
    // are pending, so a mid-phrase reload after the last flush still records it.
    const outgoing: Record<string, unknown>[] = [];
    if (this.context) {
      const snap = this.getInterruptionSnapshot?.() ?? null;
      // Mint ONCE per interruption episode (see the field doc), then reuse across the
      // 2-3 duplicate teardown fires so they dedup to one row. resetInterruptionEpisode
      // (foreground-return) / configure (new session) / stop clear it.
      this.interruptionClientEventId ??= uuidv4();
      const interruptionClientEventId = this.interruptionClientEventId;
      outgoing.push({
        ...this.buildBase(),
        client_event_id: interruptionClientEventId,
        event_type: EventType.SessionInterruptionEvent,
        play_session_id: this.context.playSessionId,
        level_number: this.context.levelNumber,
        tier_number: this.context.tierNumber,
        phrase_s3_id: snap?.phraseS3Id,
        position: snap?.position,
      });
    }
    for (const b of this.pending.values()) outgoing.push(b.event);
    if (outgoing.length === 0) return;
    const body = JSON.stringify(outgoing);

    const supportsKeepalive = typeof fetch === 'function';
    if (supportsKeepalive) {
      let token: string | undefined;
      try {
        token = await getCurrentAuthToken();
      } catch {
        token = undefined;
      }
      try {
        await fetch(this.eventsUrl, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body,
        });
        return;
      } catch (err) {
        logger.warn('[play-time-flush] keepalive fetch failed', err);
      }
    }

    // Last resort: sendBeacon cannot set an Authorization header, so this only lands
    // if the endpoint can accept it; used solely where keepalive fetch is unavailable.
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(this.eventsUrl, new Blob([body], { type: 'application/json' }));
      }
    } catch (err) {
      logger.warn('[play-time-flush] sendBeacon fallback failed', err);
    }
  }
}

// MUS-473: the teardown interruption snapshot the play screen configures. It
// reads the phrase in progress (and the position inside it) off the live event
// stream. While the stream holds no phrase - before the first is laid out, or
// after a session or tier reset empties it - the event carries no phrase rather
// than a stale one.
export const phraseInProgressSnapshot =
  (stream: Pick<EventStream, 'getPhraseInProgress'>) =>
  (): { phraseS3Id?: string; position?: number } | null =>
    stream.getPhraseInProgress() ?? null;

// Singleton - one active play session at a time.
export const playTimeNoteFlushController = new PlayTimeNoteFlushController();
